import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import RenderDynamicPopupPreview from "../../components/Preview/RenderDynamicPopupPreview";
import { AppProvider, useApp } from "../../context/AppState";
import useEmbedding from "../../hooks/useEmbedding";
import { useAnalytics } from "../../hooks/useAnalytics";
import { trackImpression } from "../../utils/analyticsUtils";
import { isPopupReadyForImpression } from "../../utils/popup-runtime-content-loading";
import { notifyPopupClosedRuntime } from "../../utils/notify-popup-closed-runtime";
import { shouldBypassEmbedPortalInEditorPreview } from "../../../shared/extension-preview.js";
import { isStorefrontProductPopup } from "../../../shared/product-upsell-recommendation-defaults.js";
import { POPUP_SECTIONS } from "../../constant/constant";
import { getStepSectionsForPopup } from "../../utils/popup-step-sections";
import cartService from "../../services/cart.service";

const RenderPopupInstance = ({
  popupState,
  setPopupState,
  popupData,
}) => {
  const embeddingData = useMemo(
    () => popupData?.generalSettings?.condition?.embed,
    [popupData]
  );

  const { appState } = useApp();
  const useFullFrameExtensionPreview = shouldBypassEmbedPortalInEditorPreview(
    popupData,
    appState
  );
  const { placeholder } = useEmbedding(
    useFullFrameExtensionPreview ? {} : embeddingData,
    popupData?._id
  );

  const { addAnalyticsEvent } = useAnalytics();
  const hasLoggedImpressionRef = useRef(false);
  const prevOpenPopupRef = useRef(popupState?.openPopup === true);

  useEffect(() => {
    if (appState?.is_preview_mode) {
      prevOpenPopupRef.current = popupState?.openPopup === true;
      return;
    }

    const wasOpen = prevOpenPopupRef.current === true;
    const isOpen = popupState?.openPopup === true;

    if (wasOpen && !isOpen && !popupState?.drawerClosing) {
      notifyPopupClosedRuntime(popupData, "user_close");
    }

    prevOpenPopupRef.current = isOpen;
  }, [
    popupState?.openPopup,
    popupState?.drawerClosing,
    popupData,
    appState?.is_preview_mode,
  ]);

  const trackPopupImpression = async () => {
    await trackImpression({
      popupId: popupData._id,
      popupType: popupData.template_id?.title,
      campaignId: popupData.campaign_Id,
      addAnalyticsEvent
    });
  };

  useEffect(() => {
    if (appState?.is_preview_mode) return;
    if (!popupState?.openPopup || hasLoggedImpressionRef.current) return;
    if (!isPopupReadyForImpression(popupData)) return;

    trackPopupImpression();
    hasLoggedImpressionRef.current = true;
  }, [popupState?.openPopup, popupData, appState?.is_preview_mode]);

  /** Common storefront revenue tracking — any addToCart while this popup is open gets pixel props. */
  useEffect(() => {
    if (!popupState?.openPopup || !isStorefrontProductPopup(popupData)) {
      cartService.setActiveStorefrontTrackingContext(null);
      return undefined;
    }

    const activeStep =
      typeof popupState?.activeStep === "number" ? popupState.activeStep : 0;
    const products = getStepSectionsForPopup(popupData, activeStep)
      .filter((section) => section?.type === POPUP_SECTIONS.PRODUCT)
      .flatMap((section) => section?.products || []);

    cartService.setActiveStorefrontTrackingContext({
      popup: popupData,
      products,
      shop: window?.Shopify?.shop || window.location.hostname,
      offerRuleId: popupData?.__weupsellMatchedOfferRuleId || undefined,
    });

    return () => {
      cartService.setActiveStorefrontTrackingContext(null);
    };
  }, [
    popupState?.openPopup,
    popupState?.activeStep,
    popupData?._id,
    popupData,
  ]);


  useEffect(() => {
    const { is_embedded } = embeddingData || {};
    const { is_preview_mode } = appState || {};

    const createPreviewWrapper = () => {
      const wrapper = document.createElement("div");
      wrapper.id = "preview-wrapper";
      Object.assign(wrapper.style, {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "fixed",
        top: "0",
        left: "0",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
      });

      const previewContainer = document.createElement("div");
      previewContainer.id = "preview-container";
      Object.assign(previewContainer.style, {
        width: "100%",
        padding: "20px",
        border: "2px dashed lightgray",
        margin: "10px 0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      });
      previewContainer.textContent = "Target Container";

      wrapper.appendChild(previewContainer);
      document.body.appendChild(wrapper);
    };

    if (is_embedded && is_preview_mode && !useFullFrameExtensionPreview) {
      if (!document.getElementById("preview-container")) {
        createPreviewWrapper();
      }
    } else {
      const wrapper = document.getElementById("preview-wrapper");
      if (wrapper) {
        wrapper.remove();
      }
    }
  }, [embeddingData, appState, useFullFrameExtensionPreview]);

  if (
    !appState?.is_preview_mode &&
    popupData?.generalSettings?.condition?.teaser?.step ===
      popupState?.activeStep &&
    !popupData?.generalSettings?.condition?.teaser?.enabled
  ) {
    return null;
  }

  if (!popupState?.openPopup && !popupState?.drawerClosing) return null;

  if (
    !useFullFrameExtensionPreview &&
    embeddingData?.is_embedded &&
    (popupState?.hasOwnProperty("isEmbeded") ? popupState.isEmbeded : true)
  ) {
    if (!placeholder) {
      return null;
    }

    return createPortal(
      <AppProvider prevState={appState}>
        <RenderDynamicPopupPreview
          popupData={popupData}
          popupState={popupState}
          setPopupState={setPopupState}
        />
      </AppProvider>,
      placeholder
    );
  }

  return (
    <>
      <RenderDynamicPopupPreview
        popupData={popupData}
        popupState={popupState}
        setPopupState={setPopupState}
      />
    </>
  );
};

export default RenderPopupInstance;
